import { useEffect, useRef } from 'react';

/**
 * 監聽外部點擊的自定義 Hook
 *
 * 當用戶點擊指定元素外部時執行回調函數，
 * 常用於關閉 dropdown、modal、popover 等浮動元素
 *
 * @param {React.RefObject} ref - React ref 物件，指向需要監聽的元素
 * @param {Function} handler - 點擊外部時執行的回調函數
 * @param {boolean} enabled - 是否啟用監聽，默認為 true
 *
 * @example
 * // 基本用法 - 關閉 dropdown
 * const dropdownRef = useRef(null);
 * const [isOpen, setIsOpen] = useState(false);
 *
 * useClickOutside(dropdownRef, () => {
 *   setIsOpen(false);
 * });
 *
 * return (
 *   <div ref={dropdownRef}>
 *     <button onClick={() => setIsOpen(!isOpen)}>Toggle</button>
 *     {isOpen && <div>Dropdown content</div>}
 *   </div>
 * );
 *
 * @example
 * // 條件性啟用 - 只在 dropdown 開啟時監聽
 * const dropdownRef = useRef(null);
 * const [isOpen, setIsOpen] = useState(false);
 *
 * useClickOutside(
 *   dropdownRef,
 *   () => setIsOpen(false),
 *   isOpen  // 只在開啟時監聽，提升性能
 * );
 *
 * @example
 * // 多個 dropdown - 獨立控制
 * const languageRef = useRef(null);
 * const userMenuRef = useRef(null);
 * const [isLanguageOpen, setIsLanguageOpen] = useState(false);
 * const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
 *
 * useClickOutside(languageRef, () => setIsLanguageOpen(false), isLanguageOpen);
 * useClickOutside(userMenuRef, () => setIsUserMenuOpen(false), isUserMenuOpen);
 */
const useClickOutside = (ref, handler, enabled = true) => {
  // 使用 ref 存儲最新的 handler，避免依賴問題
  const savedHandler = useRef(handler);

  // 每次渲染時更新 savedHandler
  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    // 如果未啟用，不註冊監聽器
    if (!enabled) {
      return;
    }

    const handleClickOutside = (event) => {
      // 檢查 ref 是否存在且點擊發生在元素外部
      if (ref.current && !ref.current.contains(event.target)) {
        savedHandler.current(event);
      }
    };

    // 監聽 mousedown 和 touchstart 支援觸控設備
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    // Cleanup: 移除事件監聽器
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [ref, enabled]); // 只依賴 ref 和 enabled，不依賴 handler
};

export default useClickOutside;
